import { writeDraft } from '../lib/draftStorage'

/**
 * draftFlushRegistry — belt-and-suspenders backup for the debounced
 * write-through primary path in useDraft.  If a chunk error hits within
 * the ~300ms debounce window (user just typed a keystroke, boundary
 * catches immediately), this registry lets the boundary flush the
 * latest snapshot synchronously before window.location.reload().
 *
 * Design after T2-B.next.B panel review — Opus 4.8 finding: on
 * NATURAL unmount (modal Cancel, route change), do a final flush
 * to localStorage.  Modal close is not a "discard" signal by itself —
 * only explicit user-triggered discard clears the draft.  This matches
 * the crash-recovery contract users expect from a legal-industry app.
 *
 * Multi-instance safe: value is Map<token, snapshotFn>, allowing
 * multiple mounted components with the same saveKey (e.g. two split
 * views of the same invoice) to each contribute a snapshot without one
 * component's cleanup deleting another's registration (Sol finding).
 */

const registry = new Map() // saveKey → Map<token, snapshotFn>

/**
 * registerFlush — register a snapshot function for `saveKey`.  Returns
 * an unregister function that, when called (typically from useEffect
 * cleanup), does a FINAL flush to localStorage before deleting the
 * entry.  This preserves last-known state on natural unmount.
 */
export function registerFlush (saveKey, snapshotFn) {
  if (!saveKey || typeof snapshotFn !== 'function') return () => {}
  const token = Symbol(saveKey)
  let byToken = registry.get(saveKey)
  if (!byToken) {
    byToken = new Map()
    registry.set(saveKey, byToken)
  }
  byToken.set(token, snapshotFn)
  return () => {
    // Final flush on natural unmount — preserves the last state a user
    // was editing when a modal closes cleanly (via ESC / backdrop
    // click / component unmount from route nav).  The debounced write
    // path may have missed the very last keystroke before unmount.
    try {
      const s = snapshotFn()
      if (s != null) writeDraft(saveKey, s)
    } catch (e) {}
    const b = registry.get(saveKey)
    if (b) {
      b.delete(token)
      if (b.size === 0) registry.delete(saveKey)
    }
  }
}

/**
 * flushAllDrafts — synchronously iterate the registry, snapshot each
 * live form, write to localStorage.  Called by ChunkLoadErrorBoundary
 * BEFORE window.location.reload().
 *
 * Never throws — one bad snapshotFn (BigInt, circular ref, throw)
 * cannot block the flush of other forms OR the subsequent reload.
 * Panel finding: this was flagged as a BLOCKER on the original design.
 */
export function flushAllDrafts () {
  for (const [saveKey, byToken] of registry) {
    for (const [, snapshotFn] of byToken) {
      try {
        const s = snapshotFn()
        if (s != null) writeDraft(saveKey, s)
      } catch (e) {
        // Swallow — best-effort backup layer
      }
    }
  }
}

// Diagnostic only — not for use in normal code paths
export function __registrySize () {
  let n = 0
  for (const [, byToken] of registry) n += byToken.size
  return n
}
